import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const MobileServiceCard = ({ 
  title, 
  lastServiceDate, 
  historicalData = [], 
  mileageInterval, 
  yearInterval, 
  description 
}) => {
  
  const calculateServiceStatus = () => {
    if (!lastServiceDate) {
      return {
        status: 'unknown',
        daysUntilDue: null,
        progressPercentage: 0,
        statusText: 'No service date recorded',
        color: 'text.disabled',
        icon: <ScheduleIcon />
      };
    }

    const lastDate = new Date(lastServiceDate);
    const today = new Date();
    const daysSinceService = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
    
    // Calculate based on time interval
    const maxDaysInterval = yearInterval * 365;
    const timeProgressPercentage = (daysSinceService / maxDaysInterval) * 100;
    
    let status, statusText, color, icon, progressPercentage;
    const daysUntilDue = maxDaysInterval - daysSinceService;

    if (timeProgressPercentage >= 100) {
      status = 'overdue';
      statusText = `Overdue by ${Math.abs(daysUntilDue)} days`;
      color = 'error.main';
      icon = <ErrorIcon />;
      progressPercentage = 100;
    } else if (timeProgressPercentage >= 80) {
      status = 'due_soon';
      statusText = `Due in ${daysUntilDue} days`;
      color = 'warning.main';
      icon = <WarningIcon />;
      progressPercentage = timeProgressPercentage;
    } else {
      status = 'good';
      statusText = `Due in ${daysUntilDue} days`;
      color = 'success.main';
      icon = <CheckIcon />;
      progressPercentage = timeProgressPercentage;
    }

    return {
      status,
      daysUntilDue,
      progressPercentage: Math.min(progressPercentage, 100),
      statusText,
      color,
      icon
    };
  };

  const serviceStatus = calculateServiceStatus();

  const getStatusChipProps = () => {
    switch (serviceStatus.status) {
      case 'overdue':
        return { label: 'Overdue', color: 'error' };
      case 'due_soon':
        return { label: 'Due Soon', color: 'warning' };
      case 'good':
        return { label: 'Good', color: 'success' };
      default:
        return { label: 'Unknown', color: 'default' };
    }
  };

  return (
    <Card className="mobile-card" sx={{ margin: '8px 16px' }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: serviceStatus.color,
              width: 40,
              height: 40,
              mr: 2
            }}
          >
            {serviceStatus.icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" className="mobile-subtitle" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <Chip
            size="small"
            {...getStatusChipProps()}
            variant="outlined"
          />
        </Box>

        {/* Progress Bar */}
        {serviceStatus.status !== 'unknown' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Service Progress
              </Typography>
              <Typography variant="body2" color={serviceStatus.color}>
                {Math.round(serviceStatus.progressPercentage)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={serviceStatus.progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: serviceStatus.color,
                  borderRadius: 3
                }
              }}
            />
          </Box>
        )}

        {/* Status Text */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color={serviceStatus.color} fontWeight="medium">
            {serviceStatus.statusText}
          </Typography>
          {lastServiceDate && (
            <Typography variant="caption" color="text.secondary">
              Last: {new Date(lastServiceDate).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* Mileage Info */}
        {mileageInterval && historicalData.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Current: {historicalData[0]?.current_odometer?.toLocaleString() || 'Unknown'} miles
              {mileageInterval && ` • Next at: ${((historicalData[0]?.current_odometer || 0) + mileageInterval).toLocaleString()} miles`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MobileServiceCard; 